import { supabase } from '@/integrations/supabase/client';

class AnalyticsTracker {
  private sessionId: string;
  private userId: string | null = null;

  constructor() {
    this.sessionId = this.getOrCreateSessionId();
    this.initializeUserId();
    this.trackPageView();
    this.setupUnloadTracking();
  }

  private getOrCreateSessionId(): string {
    let sessionId = sessionStorage.getItem('safurra_session_id');
    if (!sessionId) {
      sessionId = 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      sessionStorage.setItem('safurra_session_id', sessionId);
    }
    return sessionId;
  }

  private async initializeUserId() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      this.userId = user?.id || null;
    } catch (error) {
      console.error('Error getting user:', error);
    }

    // Listen for auth changes
    supabase.auth.onAuthStateChange((event, session) => {
      this.userId = session?.user?.id || null;
    });
  }

  private async sendToAnalytics(action: string, data: any) {
    try {
      const payload = {
        action,
        data: {
          ...data,
          sessionId: this.sessionId,
          userId: this.userId,
        }
      };

      await supabase.functions.invoke('analytics', {
        body: payload
      });
    } catch (error) {
      console.error('Analytics error:', error);
    }
  }

  // Track page views
  trackPageView(route?: string) {
    const currentRoute = route || window.location.pathname;
    this.sendToAnalytics('track_event', {
      eventName: 'page_view',
      eventType: 'page_view',
      pageRoute: currentRoute,
      additionalData: {
        title: document.title,
        referrer: document.referrer,
      }
    });
  }

  // Track button clicks and interactions
  trackClick(elementName: string, elementValue?: string, additionalData?: any) {
    this.sendToAnalytics('track_event', {
      eventName: 'click',
      eventType: 'click',
      pageRoute: window.location.pathname,
      elementClicked: elementName,
      elementValue: elementValue,
      additionalData: additionalData,
    });
  }

  // Track form interactions
  trackFormInteraction(formName: string, fieldName: string, action: string, value?: string) {
    this.sendToAnalytics('track_event', {
      eventName: 'form_interaction',
      eventType: 'form_interaction',
      pageRoute: window.location.pathname,
      elementClicked: `${formName}_${fieldName}`,
      elementValue: value,
      additionalData: {
        formName,
        fieldName,
        action, // 'focus', 'change', 'submit', etc.
      }
    });
  }

  // Track funnel progress
  trackFunnelStep(step: 'started_form' | 'completed_form' | 'entered_chat' | 'completed_chat' | 'quote_delivered' | 'referral_link_clicked' | 'account_created') {
    this.sendToAnalytics('track_funnel', {
      step,
    });
  }

  // Track referral link clicks
  trackReferralClick(carrierName: string, referralUrl: string) {
    this.sendToAnalytics('track_referral', {
      carrierName,
      referralUrl,
    });
  }

  // Track custom events
  trackCustomEvent(eventName: string, eventType: string, additionalData?: any) {
    this.sendToAnalytics('track_event', {
      eventName,
      eventType,
      pageRoute: window.location.pathname,
      additionalData,
    });
  }

  // Track dropdown selections
  trackDropdownSelection(dropdownName: string, selectedValue: string) {
    this.sendToAnalytics('track_event', {
      eventName: 'dropdown_selection',
      eventType: 'form_interaction',
      pageRoute: window.location.pathname,
      elementClicked: dropdownName,
      elementValue: selectedValue,
      additionalData: {
        dropdownName,
        selectedValue,
      }
    });
  }

  // Track when user gets a quote
  trackQuoteReceived(quoteData: any) {
    this.trackFunnelStep('quote_delivered');
    this.sendToAnalytics('track_event', {
      eventName: 'quote_received',
      eventType: 'conversion',
      pageRoute: window.location.pathname,
      additionalData: {
        ...quoteData,
        timestamp: new Date().toISOString(),
      }
    });
  }

  // Track exit/abandonment
  private setupUnloadTracking() {
    window.addEventListener('beforeunload', () => {
      // Track page exit
      navigator.sendBeacon('/analytics', JSON.stringify({
        action: 'track_event',
        data: {
          sessionId: this.sessionId,
          userId: this.userId,
          eventName: 'page_exit',
          eventType: 'navigation',
          pageRoute: window.location.pathname,
          additionalData: {
            timeOnPage: Date.now() - (this.pageLoadTime || Date.now()),
          }
        }
      }));
    });

    // Track when user becomes idle
    let idleTimer: NodeJS.Timeout;
    const resetIdleTimer = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        this.trackCustomEvent('user_idle', 'engagement', {
          idleTimeMinutes: 5,
          pageRoute: window.location.pathname,
        });
      }, 5 * 60 * 1000); // 5 minutes
    };

    ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'].forEach(event => {
      document.addEventListener(event, resetIdleTimer, true);
    });
  }

  private pageLoadTime = Date.now();

  // Get session ID for external use
  getSessionId(): string {
    return this.sessionId;
  }

  // Get user ID for external use
  getUserId(): string | null {
    return this.userId;
  }
}

// Create global analytics instance
export const analytics = new AnalyticsTracker();

// Helper functions for easy tracking
export const trackClick = (elementName: string, elementValue?: string, additionalData?: any) => {
  analytics.trackClick(elementName, elementValue, additionalData);
};

export const trackFormInteraction = (formName: string, fieldName: string, action: string, value?: string) => {
  analytics.trackFormInteraction(formName, fieldName, action, value);
};

export const trackFunnelStep = (step: 'started_form' | 'completed_form' | 'entered_chat' | 'completed_chat' | 'quote_delivered' | 'referral_link_clicked' | 'account_created') => {
  analytics.trackFunnelStep(step);
};

export const trackReferralClick = (carrierName: string, referralUrl: string) => {
  analytics.trackReferralClick(carrierName, referralUrl);
};

export const trackDropdownSelection = (dropdownName: string, selectedValue: string) => {
  analytics.trackDropdownSelection(dropdownName, selectedValue);
};

export const trackQuoteReceived = (quoteData: any) => {
  analytics.trackQuoteReceived(quoteData);
};

export const trackCustomEvent = (eventName: string, eventType: string, additionalData?: any) => {
  analytics.trackCustomEvent(eventName, eventType, additionalData);
};